import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { deviceService } from "@/services/gap-device-service"
import type { GapDeviceConfig, CreateGapDeviceRequest, UpdateGapDeviceRequest } from "@/types/gap-device"
import type { BulkOperationResult } from "@/types/api"
import type { ImportProgress, ImportResult } from "@/types/bulk-import"

// Fetch devices by client (or all devices if clientId is null/empty)
export function useDevicesByClient(_clientId: string | null) {
  // Always fetch all devices since backend doesn't support filtering
  return useSWR(["devices", "all"], () => deviceService.getByClient("all"), {
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  })
}

// Fetch single device
export function useDevice(deviceId: string | null) {
  return useSWR(deviceId ? ["devices", deviceId] : null, () => deviceService.getById(deviceId!), {
    revalidateOnFocus: false,
  })
}

// Create device mutation
export function useCreateDevice() {
  return useSWRMutation(
    ["devices"],
    async (_key: string[], { arg }: { arg: CreateGapDeviceRequest }): Promise<GapDeviceConfig> => {
      return deviceService.create(arg)
    },
  )
}

// Update device mutation
export function useUpdateDevice() {
  return useSWRMutation(
    ["devices"],
    async (
      _key: string[],
      { arg }: { arg: { deviceId: string; data: UpdateGapDeviceRequest } },
    ): Promise<GapDeviceConfig> => {
      return deviceService.update(arg.deviceId, arg.data)
    },
  )
}

// Delete device mutation
export function useDeleteDevice() {
  return useSWRMutation(["devices"], async (_key: string[], { arg }: { arg: string }): Promise<void> => {
    return deviceService.delete(arg)
  })
}

// Bulk update devices mutation - each device gets full update payload
export function useBulkUpdateDevices() {
  return useSWRMutation(
    ["devices"],
    async (
      _key: string[],
      { arg }: { arg: { updates: Array<{ deviceId: string; data: UpdateGapDeviceRequest }> } },
    ): Promise<BulkOperationResult> => {
      return deviceService.bulkUpdate(arg.updates)
    },
  )
}

// Bulk delete devices mutation
export function useBulkDeleteDevices() {
  return useSWRMutation(
    ["devices"],
    async (_key: string[], { arg }: { arg: string[] }): Promise<BulkOperationResult> => {
      return deviceService.bulkDelete(arg)
    },
  )
}

// Bulk import options
export interface BulkImportOptions {
  onProgress?: (progress: ImportProgress) => void
  onDeviceComplete?: (result: ImportResult) => void
}

// Bulk import devices mutation - iterates through devices one by one
export function useBulkImportDevices() {
  return useSWRMutation(
    ["devices", "bulk-import"],
    async (
      _key: string[],
      { arg }: { arg: { devices: CreateGapDeviceRequest[]; options?: BulkImportOptions } },
    ): Promise<ImportProgress> => {
      const { devices, options } = arg
      const progress: ImportProgress = {
        total: devices.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
        currentDeviceId: null,
        results: [],
      }

      for (const device of devices) {
        progress.currentDeviceId = device.deviceId
        options?.onProgress?.({ ...progress })

        try {
          await deviceService.create(device)
          progress.succeeded++
          const result: ImportResult = { deviceId: device.deviceId, success: true }
          progress.results.push(result)
          options?.onDeviceComplete?.(result)
        } catch (error) {
          progress.failed++
          const errorMessage = error instanceof Error ? error.message : "Unknown error"
          const result: ImportResult = {
            deviceId: device.deviceId,
            success: false,
            error: errorMessage,
          }
          progress.results.push(result)
          options?.onDeviceComplete?.(result)
        }

        progress.completed++
        options?.onProgress?.({ ...progress })
      }

      progress.currentDeviceId = null
      return progress
    },
  )
}
